import React, { useState } from 'react';

import { Alert, Anchor, H3 } from '@zooplus/ui-components';

import CreditCard from '@components/CreditCard/CreditCard';
import { useTranslation } from '@hooks/useTranslations';
import { useCreditcardService } from '@providers/CreditCardsMutationProvider';
import { useErrorContext } from '@providers/ErrorContextProvider';
import { OverlayIds, useOverlays } from '@providers/OverlaysProvider';
import AdobeTrackingService from '@services/AdobeTrackingService';
import { captureException } from '@services/sentryService';
import { CreditCardData } from '@types-internal';

import styles from './StoredCreditCards.module.scss';

export type StoredCreditCardsProps = {
  isMyAccount: boolean;
  isFallbackPsp: boolean;
  isBancontact?: boolean;
};

const StoredCreditCards: React.FC<StoredCreditCardsProps> = ({
  isMyAccount,
  isFallbackPsp,
  isBancontact = false,
}) => {
  const { showOverlay } = useOverlays();
  const { t } = useTranslation();
  const { setError } = useErrorContext();
  const [loadingCardPspIdentifier, setLoadingCardPspIdentifier] = useState<string>();

  const {
    initiateCardDeletion,
    makeCreditCardActive,
    data: { creditCards },
  } = useCreditcardService();

  /**
   * Send a tracking event after selecting a new Adyen card. This tracking will be trigger
   * in edit payment method page (my account), and edit payment page (checkout) when the
   * user click to select a card.
   */
  const sendCardChangedTracking = () => {
    AdobeTrackingService.accountEventTracking('change_payment_method', {
      change_payment_detail: true,
      change_payment_method: false,
      new_payment_method: 'AK',
    });
  };

  const handleSelectCard = (creditCard: CreditCardData) => () => {
    if (!creditCard.selected) {
      if (creditCard.expired) {
        setError({
          message: t('checkout.payment.creditcard.error.selectcard.expired'),
          retryable: true,
        });
      } else {
        setError(undefined);
        setLoadingCardPspIdentifier(creditCard.pspIdentifier);

        makeCreditCardActive(creditCard)
          .then(isSuccess => {
            if (isSuccess) {
              sendCardChangedTracking();
            } else {
              setError({
                message: t('checkout.payment.creditcard.error.selectcard.apicall'),
                retryable: true,
              });
            }
            setLoadingCardPspIdentifier(undefined);
          })
          .catch((error: unknown) => {
            // on any unexpected error, send it to Sentry and display a generic error message to the user
            captureException({ error });
            setError({
              message: t('checkout.payment.creditcard.error.selectcard.apicall'),
              retryable: true,
            });
            setLoadingCardPspIdentifier(undefined);
          });
      }
    }
  };

  const handleDeleteCard =
    (creditCard: CreditCardData) => (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      initiateCardDeletion(creditCard);
    };

  const handleDeleteAllCards = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    showOverlay(OverlayIds.DELETE_ALL_CARDS);
  };

  return (
    <div className={styles.container}>
      {isMyAccount && !isFallbackPsp && (
        <Alert className={styles.messageCanAddCard} variant="info" data-zta="messageCanAddCard">
          {t('mzp.payment.adyen.no.card.message')}
        </Alert>
      )}
      {(creditCards.length > 0 || !isMyAccount) && (
        <>
          <H3 className={styles.heading}>{t('checkout.payment.creditcard.storedcards.header')}</H3>
          <div className={styles.creditCards}>
            {isBancontact && (
              <CreditCard
                key={creditCards[0].pspIdentifier}
                creditCard={creditCards[0]}
                onDelete={handleDeleteCard(creditCards[0])}
                onSelect={handleSelectCard(creditCards[0])}
                showIcons
                loadingCardPspIdentifier={loadingCardPspIdentifier}
              />
            )}
            {!isBancontact &&
              creditCards.map(creditCard => (
                <CreditCard
                  key={creditCard.pspIdentifier}
                  creditCard={creditCard}
                  onDelete={handleDeleteCard(creditCard)}
                  onSelect={handleSelectCard(creditCard)}
                  showIcons
                  loadingCardPspIdentifier={loadingCardPspIdentifier}
                />
              ))}
            <div className={styles.creditCardSpacer} />
          </div>
          {!isBancontact && (
            <div className={styles.deleteAll}>
              <Anchor
                className={styles.grayLink}
                data-zta="removeAllCC"
                onClick={handleDeleteAllCards}>
                {t('checkout.payment.creditcard.deleteAll')}
              </Anchor>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StoredCreditCards;
